import type { Sportsbook } from '@/types/types'
import type { GlobalBetSlipItem } from './GlobalBetSlipManager'
import * as Accordion from '@radix-ui/react-accordion'
import { useState } from 'react'

import { useApprovals } from '../hooks/useApprovals'
import { useGeo } from '../hooks/useGeo'
import { trackPlaceBet } from '../lib/analytics'
import { type BetGateResult, gateBet, pickAllowedVariant } from '../lib/gating'
import { globalBetSlipManager, useGlobalBetSlip } from './GlobalBetSlipManager'
import { hasLogo, LogoMark } from './sportsbookLogos'
import { useUIPreferences } from './UIPreferencesManager'

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  )
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  )
}

// Brand metadata (logo + colors + CTA copy) is sourced from the API
// (Supabase sportsbooks catalog). The overlay looks up per-bet brand by
// (league, sportsbook) via the GlobalBetSlipManager — brand fields are
// the same across leagues for a given book, so any active league for
// that book yields the right metadata.

const USD_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function formatMoney(amount: number): string {
  let normalized = Number(amount)
  if (!Number.isFinite(normalized))
    normalized = 0
  if (Object.is(normalized, -0))
    normalized = 0
  return USD_FORMATTER.format(normalized)
}

function calculateSinglePayout(odds: { american: string, decimal: string }, stake: number): number {
  if (stake === 0)
    return 0

  const americanOdds = odds.american
  // Strip both ASCII '-' and Unicode '−' as well as '+' to get absolute value
  const cleanOdds = americanOdds.replace(/[+\-−]/g, '')
  const numericOdds = Number.parseFloat(cleanOdds) || 0

  if (numericOdds === 0)
    return 0

  const hasMinus = americanOdds.includes('-') || americanOdds.includes('−')
  return hasMinus
    ? stake * (100 / numericOdds)
    : stake * (numericOdds / 100)
}

function calculatePayout(bets: GlobalBetSlipItem[]) {
  return bets.reduce((total, bet) => {
    const stake = Number.parseFloat(bet.stake) || 0
    return total + calculateSinglePayout(bet.odds, stake)
  }, 0)
}

// The gate judges by sportsbook availability only (2026-07-02 policy change),
// so there is a single blocked-message variant. Per-sport approval copy
// ("PFL isn't approved for betting in …") retired with the sport check.
function buildBlockedMessage(
  _reason: Exclude<BetGateResult['reason'], null>,
  bet: GlobalBetSlipItem,
  region: string,
): string {
  return `${bet.sportsbook} isn't licensed in ${region}`
}

function BlockedBetMessage({ message }: { message: string }) {
  return (
    <div
      className="flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 text-xs leading-snug dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
      role="status"
    >
      <span aria-hidden="true" className="text-base leading-none mt-0.5">⚠</span>
      <span>{message}</span>
    </div>
  )
}

function GlobalBetSlipOverlay() {
  const [openPanel, setOpenPanel] = useState<string>('')
  const { oddsFormat, setOddsFormat } = useUIPreferences()
  const { data: geo } = useGeo()
  const { data: approvals } = useApprovals()

  const {
    betSlip,
    lastAddedSportsbook,
    removeBet,
    updateStake,
    clearSportsbook,
    getSportsbookLink,
  } = useGlobalBetSlip()

  const [prevLastAddedSportsbook, setPrevLastAddedSportsbook] = useState(lastAddedSportsbook)
  if (lastAddedSportsbook !== prevLastAddedSportsbook) {
    setPrevLastAddedSportsbook(lastAddedSportsbook)
    if (lastAddedSportsbook) {
      setOpenPanel(`panel-${lastAddedSportsbook}`)
    }
  }

  // Calculate total bets across all sportsbooks
  const allBets = [
    ...betSlip.DraftKings,
    ...betSlip.FanDuel,
    ...betSlip.Bet365,
    ...betSlip.BetMGM,
    ...betSlip.Polymarket,
    ...betSlip.Stake,
    ...betSlip.Underdog,
  ]

  // Don't render if no bets
  if (allBets.length === 0) {
    return null
  }

  // Underdog picks show their payout multiplier (decimal + "x"), the only
  // price Underdog's own app displays — mirrors Widget.tsx:displayOdds.
  const formatOddsDisplay = (bet: GlobalBetSlipItem): string => {
    if (bet.sportsbook === 'Underdog')
      return `${bet.odds.decimal}x`
    if (oddsFormat === 'american') {
      return bet.odds.american
    }
    else {
      return bet.odds.decimal
    }
  }

  const handlePlaceIndividualBet = (bet: GlobalBetSlipItem) => {
    // Canonical aliases (e.g. "Stake") map to multiple geo-targeted catalog
    // variants. Pick the variant approved for the visitor and route to its
    // affiliate URL from the meta's `variants` map.
    const meta = globalBetSlipManager.getSportsbookMeta(bet.league, bet.sportsbook)
    let url: string | undefined
    if (meta?.variants) {
      const variant = pickAllowedVariant(bet.sportsbook, geo, approvals)
      url = variant ? meta.variants[variant] : undefined
    }
    else {
      url = getSportsbookLink(bet.league, bet.sportsbook)
    }
    if (url) {
      trackPlaceBet(bet.league, bet.sportsbook, bet.selectionName, bet.marketName, bet.eventName)
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    else {
      console.warn(`No affiliate link configured for ${bet.sportsbook} in league "${bet.league}"`)
    }
  }

  // Get sportsbooks that have bets
  const sportsbooks = (Object.keys(betSlip) as Array<keyof typeof betSlip>).filter(
    (sportsbook): sportsbook is Sportsbook =>
      sportsbook !== 'lastAddedSportsbook' && betSlip[sportsbook].length > 0,
  )

  return (
    <div className="pointer-events-auto fixed bottom-0 left-0 right-0 z-50 w-full md:top-4 md:bottom-auto md:right-4 md:left-auto md:w-96 md:max-h-[500px]">
      <div className="backdrop-blur-sm bg-white/95 rounded-lg shadow-2xl border border-gray-200 overflow-hidden max-h-[400px] md:max-h-[500px] flex flex-col dark:bg-(--ow-surface) dark:border-(--ow-border) dark:shadow-[0_4px_14px_rgba(0,0,0,0.55)]">

        {/* Header with total bet count and odds format toggle */}
        <div className="p-3 border-b border-gray-200 flex items-center justify-between bg-gray-50 dark:border-(--ow-hairline) dark:bg-(--ow-inset)">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900 dark:text-(--ow-ink)">Bet Slip</h3>
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded-full dark:bg-(--ow-accent-soft) dark:text-(--ow-link)">
              {allBets.length}
              {' '}
              bet
              {allBets.length !== 1 ? 's' : ''}
            </span>
          </div>
          <select
            value={oddsFormat}
            onChange={e => setOddsFormat(e.target.value as 'american' | 'decimal')}
            className="text-xs px-2 py-1 border border-[var(--lp-accent,#0db5ff)] rounded focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-(--ow-inset) dark:focus:ring-(--ow-accent)"
          >
            <option value="american">American</option>
            <option value="decimal">Decimal</option>
          </select>
        </div>

        {/* Accordion for sportsbooks */}
        <Accordion.Root
          type="single"
          collapsible
          value={openPanel}
          onValueChange={setOpenPanel}
          className="w-full overflow-y-auto flex-1"
        >
          {sportsbooks.map((sportsbook) => {
            const sportsbookBets = betSlip[sportsbook]
            const firstBet = sportsbookBets[0]
            const meta = firstBet
              ? globalBetSlipManager.getSportsbookMeta(firstBet.league, sportsbook)
              : undefined
            const placeBetLabel = meta?.place_bet_label ?? 'Place Bet'
            const panelId = `panel-${sportsbook}`
            const groupedBets = sportsbookBets.reduce((groups, bet) => {
              const key = `${bet.eventName}-${bet.marketName}`
              if (!groups[key])
                groups[key] = []
              groups[key].push(bet)
              return groups
            }, {} as Record<string, GlobalBetSlipItem[]>)
            const hasMultipleEvents = Object.keys(groupedBets).length > 1

            return (
              <Accordion.Item key={sportsbook} value={panelId} className="border-b border-gray-200 last:border-b-0 dark:border-(--ow-hairline)">
                <Accordion.Header className="flex">
                  <Accordion.Trigger
                    className="flex flex-1 items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors duration-150 dark:hover:bg-(--ow-inset)"
                  >
                    <div className="flex items-center gap-3">
                      {hasLogo(sportsbook, meta)
                        ? (
                            <div
                              className="w-8 h-8 flex-shrink-0 rounded overflow-hidden flex items-center justify-center"
                              style={meta?.brand_color ? { background: meta.brand_color } : undefined}
                            >
                              <LogoMark sportsbook={sportsbook} meta={meta} className={`w-full h-full ${meta?.logo_fill ? 'object-cover' : 'object-contain'}`} />
                            </div>
                          )
                        : <span className="w-8 h-8 flex items-center justify-center text-xs font-bold bg-gray-100 rounded dark:bg-(--ow-inset)">{sportsbook.slice(0, 2)}</span>}
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">{sportsbook}</span>
                        <span className="text-xs text-gray-600 dark:text-(--ow-ink-2)">
                          Winnings:
                          {' '}
                          <span className="font-bold text-green-600 dark:text-green-400">
                            {formatMoney(calculatePayout(sportsbookBets))}
                          </span>
                        </span>
                      </div>
                      <span className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded-full dark:bg-(--ow-inset) dark:text-(--ow-ink-2)">
                        {sportsbookBets.length}
                      </span>
                    </div>
                    <ChevronDownIcon
                      className={`w-5 h-5 text-gray-500 dark:text-(--ow-meta) transition-transform duration-200 ${
                        openPanel === panelId ? 'rotate-180' : ''
                      }`}
                    />
                  </Accordion.Trigger>
                </Accordion.Header>

                <Accordion.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                  <div className="p-4 pt-0 space-y-4">

                    {/* Group bets by event for better organization */}
                    {Object.entries(groupedBets).map(([eventKey, eventBets]) => (
                      <div key={eventKey} className="space-y-2">

                        {/* Event header (only show if multiple events) */}
                        {hasMultipleEvents && eventBets[0] && (
                          <div className="text-xs font-medium text-gray-600 border-b border-gray-100 pb-1 dark:text-(--ow-ink-2) dark:border-(--ow-hairline)">
                            {eventBets[0].eventName}
                            {' '}
                            •
                            {eventBets[0].marketName}
                          </div>
                        )}

                        {/* Bets for this event */}
                        {eventBets.map((bet) => {
                          // Compute the gate once per bet so the stake row stays clean
                          // when allowed, and the blocked message gets its own row
                          // below the stake when not.
                          const gate = gateBet(bet.sportsbook, geo, approvals)
                          const isBlocked = !gate.allowed && gate.reason !== null
                          const region = geo?.region ?? geo?.country ?? 'your area'
                          return (
                            <div key={bet.id} className="bg-gray-50 rounded-lg p-3 space-y-2 dark:bg-(--ow-inset)">
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-gray-900 text-sm truncate dark:text-(--ow-ink)">
                                    {bet.selectionName}
                                  </div>
                                  <div className="text-xs text-gray-500 truncate dark:text-(--ow-meta)">
                                    {bet.eventName}
                                    {' '}
                                    •
                                    {bet.marketName}
                                  </div>
                                  <div className="text-sm font-semibold text-green-600 mt-1 dark:text-green-400">
                                    {formatOddsDisplay(bet)}
                                  </div>
                                  {bet.stake && Number.parseFloat(bet.stake) > 0 && (
                                    <div className="text-sm text-blue-600 font-medium mt-1 dark:text-(--ow-link)">
                                      Winnings:
                                      {' '}
                                      {formatMoney(calculateSinglePayout(bet.odds, Number.parseFloat(bet.stake)))}
                                    </div>
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeBet(bet.id, bet.sportsbook)}
                                  className="text-gray-400 hover:text-red-500 transition-colors duration-150 ml-2 dark:text-(--ow-meta) dark:hover:text-red-400"
                                  aria-label={`Remove ${bet.selectionName} bet`}
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>

                              {/* Stake Input + Place Bet button (when allowed) */}
                              <div className="flex items-center gap-2">
                                <label htmlFor={`stake-${bet.id}`} className="text-xs text-gray-600 whitespace-nowrap dark:text-(--ow-ink-2)">
                                  Stake:
                                </label>
                                <div className="flex-1 relative">
                                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm dark:text-(--ow-meta)">
                                    $
                                  </span>
                                  <input
                                    id={`stake-${bet.id}`}
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={bet.stake}
                                    onChange={e => updateStake(bet.id, e.target.value, bet.sportsbook)}
                                    className="w-full pl-6 pr-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:border-(--ow-border) dark:bg-(--ow-surface) dark:focus:ring-(--ow-accent)"
                                    placeholder="0.00"
                                  />
                                </div>
                                {!isBlocked && (
                                  <div>
                                    <button
                                      onClick={() => handlePlaceIndividualBet(bet)}
                                      style={meta?.secondary_color ? { background: meta.secondary_color } : undefined}
                                      className="w-full font-semibold py-2 px-3 rounded-lg hover:opacity-90 active:scale-95 transition-all duration-150 text-sm bg-gray-100 text-gray-900"
                                      type="button"
                                    >
                                      {placeBetLabel}
                                    </button>
                                  </div>
                                )}
                              </div>

                              {/* Blocked-bet message — own row below the stake.
                                  Odds remain visible above; only the action is gated. */}
                              {isBlocked && (
                                <BlockedBetMessage message={buildBlockedMessage(gate.reason!, bet, region)} />
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ))}

                    {/* Footer Actions */}
                    <div className="flex justify-center pt-2 border-t border-gray-200 dark:border-(--ow-hairline)">
                      <button
                        onClick={() => clearSportsbook(sportsbook)}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:scale-95 transition-all duration-150 text-sm dark:border-(--ow-border) dark:text-(--ow-ink-2) dark:hover:bg-(--ow-inset)"
                        type="button"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                </Accordion.Content>
              </Accordion.Item>
            )
          })}
        </Accordion.Root>
      </div>
    </div>
  )
}

export default GlobalBetSlipOverlay
